import React, { useEffect, useRef, useState } from "react";
import {
  attachmentDownloadUrl,
  deleteAttachment,
  listAttachmentsForItem,
  uploadAttachment,
} from "../../api/boardApi";
import { errorToast } from "../../../../redux/actionCreator";
import AttachmentPreview from "../AttachmentPreview";

// File column cell — shows attachment chips and an upload button. Click a
// chip to download; hover and click ✕ to delete. boardId + itemId come via
// the registry (CellRenderer forwards `extra`).
//
// The cell `value` is just an array of attachment _ids; we fetch fresh
// metadata once on mount so chips show filenames/sizes. To stay snappy on
// large boards we cache the resolved list in a module-level Map.
const metaCache = new Map();

export default function FileCell({ value, readOnly, column, item, boardId }) {
  const ids = Array.isArray(value) ? value : value ? [value] : [];
  const [meta, setMeta] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!item?._id || ids.length === 0) {
      setMeta([]);
      return;
    }
    const cacheKey = String(item._id);
    if (metaCache.has(cacheKey)) {
      const cached = metaCache.get(cacheKey);
      // Filter to ids we still have on this column.
      setMeta(cached.filter((a) => ids.includes(String(a._id))));
    }
    listAttachmentsForItem(boardId, item._id)
      .then((res) => {
        const filtered = (res.data || []).filter(
          (a) => String(a.column_id) === String(column._id) && ids.includes(String(a._id)),
        );
        metaCache.set(cacheKey, res.data || []);
        setMeta(filtered);
      })
      .catch(() => { /* leave stale meta */ });
  }, [ids.join(","), boardId, item?._id, column._id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !item?._id) return;
    setUploading(true);
    try {
      const res = await uploadAttachment(boardId, item._id, column._id, file);
      setMeta((arr) => [...arr, res.data]);
      // Bust cache so other consumers re-fetch.
      metaCache.delete(String(item._id));
    } catch (err) {
      errorToast(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await deleteAttachment(boardId, att._id);
      setMeta((arr) => arr.filter((x) => String(x._id) !== String(att._id)));
      metaCache.delete(String(item._id));
    } catch (err) {
      errorToast(err.message);
    }
  };

  return (
    <div className="cell-display file">
      {meta.length === 0 && !uploading && (
        <span className="placeholder" onClick={() => !readOnly && inputRef.current?.click()}>
          {readOnly ? "—" : "+ Upload"}
        </span>
      )}
      {meta.map((a) => (
        <span key={a._id} className="file-chip" title={`${a.filename} (${formatSize(a.size)})`}>
          <button
            type="button"
            className="file-chip-link"
            onClick={(e) => {
              e.stopPropagation();
              setPreview({
                url: attachmentDownloadUrl(boardId, a._id),
                filename: a.filename,
                mime: a.mime,
              });
            }}
          >
            📎 {truncate(a.filename, 24)}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="file-chip-del"
              onClick={(e) => { e.stopPropagation(); handleDelete(a); }}
              title="Delete"
            >
              ✕
            </button>
          )}
        </span>
      ))}
      {!readOnly && meta.length > 0 && (
        <button type="button" className="file-add" onClick={() => inputRef.current?.click()} title="Upload another">+</button>
      )}
      {uploading && <span className="file-uploading">Uploading…</span>}
      <input ref={inputRef} type="file" hidden onChange={handleUpload} />
      {preview && (
        <AttachmentPreview
          url={preview.url}
          filename={preview.filename}
          mime={preview.mime}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
function truncate(s, n) {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
